import { useMemo } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { useDocumentViewport } from "@/features/document-viewport/context";
import {
  formatDisplayedMeasurement,
  getMeasurementLabelPosition,
  getMeasurementLeaderAnchor,
  hexToRgba,
  shouldShowMeasurementLeader,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  type MeasurementAnnotation,
  type MeasurementOverlayRecord,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import { TOOL_IDS } from "@/viewport-tools";

interface ReadOnlyDetection {
  _id: Id<"documentIntelligenceDetections">;
  rawText: string;
  normalizedText: string;
  bbox: { x: number; y: number; width: number; height: number };
}

interface ReadOnlyTag {
  _id: Id<"takeoffPilotExtractedTags">;
  rawValue: string;
  normalizedValue: string;
  colorHex?: string;
}

/**
 * Read-only takeoff overlay payload for one drawing revision page.
 * @property jobId Source TakeoffPilot job backing the layer.
 * @property jobName Display name for the source TakeoffPilot job.
 * @property tagCount Number of included tags on the source job.
 * @property measurementCount Number of renderable measurements on the active page.
 * @property tags Included tags used to color and label overlay geometry.
 * @property detections Renderable page-level tag detections in normalized page coordinates.
 * @property measurements Renderable page-level measurements in normalized page coordinates.
 */
export interface ReadOnlyTakeoffLayerData {
  jobId: Id<"takeoffPilotJobs">;
  jobName: string;
  tagCount: number;
  measurementCount: number;
  tags: ReadOnlyTag[];
  detections: ReadOnlyDetection[];
  measurements: MeasurementOverlayRecord[];
}

interface ReadOnlyTakeoffLayerProps {
  layer: ReadOnlyTakeoffLayerData;
}

type MeasurementScreenPath =
  | { kind: "rect"; rect: { x: number; y: number; width: number; height: number } }
  | { kind: "poly"; points: string };

function isOpenPathTool(tool: MeasurementOverlayRecord["tool"]) {
  return tool === TOOL_IDS.line || tool === TOOL_IDS.polyline;
}

function getTagColor(tag: ReadOnlyTag | undefined) {
  return tag?.colorHex ?? "#0EA5E9";
}

function getMeasurementScreenPath(
  measurement: MeasurementOverlayRecord,
  viewport: ReturnType<typeof useDocumentViewport>
): MeasurementScreenPath {
  if (measurement.geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    const rect = viewport.transforms.pageRectToScreenRect(measurement.geometry);
    return { kind: "rect", rect };
  }

  return {
    kind: "poly",
    points: measurement.geometry.vertices
      .map((vertex) => viewport.transforms.pageToScreenPoint(vertex))
      .map((point) => `${point.x},${point.y}`)
      .join(" "),
  };
}

/** Renders a non-interactive TakeoffPilot tag and measurement layer over the active viewport page. */
export function ReadOnlyTakeoffLayer({ layer }: ReadOnlyTakeoffLayerProps) {
  const viewport = useDocumentViewport();
  const tagById = useMemo(
    () => new Map(layer.tags.map((tag) => [String(tag._id), tag])),
    [layer.tags]
  );
  const tagByText = useMemo(() => {
    const map = new Map<string, ReadOnlyTag>();
    for (const tag of layer.tags) {
      map.set(tag.normalizedValue, tag);
      map.set(tag.rawValue.trim(), tag);
    }
    return map;
  }, [layer.tags]);

  if (layer.detections.length === 0 && layer.measurements.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-10" aria-hidden="true">
      {layer.detections.map((detection) => {
        const rect = viewport.transforms.pageRectToScreenRect(detection.bbox);
        const tag = tagByText.get(detection.normalizedText) ?? tagByText.get(detection.rawText.trim());
        const color = getTagColor(tag);
        return (
          <div
            key={detection._id}
            className="absolute overflow-hidden rounded-[10px] border-2 shadow-[0_0_0_1px_rgba(15,23,42,0.16)]"
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.width,
              height: rect.height,
              borderColor: color,
              backgroundColor: hexToRgba(color, 0.14),
            }}
          >
            {rect.width >= 74 && rect.height >= 30 ? (
              <div className="absolute inset-0 flex flex-col justify-between px-2 py-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-foreground/90">
                <span className="truncate">{detection.rawText}</span>
                <span className="truncate text-[9px] text-muted-foreground">Takeoff tag</span>
              </div>
            ) : null}
          </div>
        );
      })}

      <svg className="absolute inset-0 h-full w-full overflow-visible">
        {layer.measurements.map((measurement) => {
          const tag = tagById.get(String(measurement.tagId ?? ""));
          const color = getTagColor(tag);
          const path = getMeasurementScreenPath(measurement, viewport);
          const labelPositionPage = getMeasurementLabelPosition(
            measurement.geometry,
            measurement.annotation
          );
          const labelPosition = viewport.transforms.pageToScreenPoint(labelPositionPage);
          const leaderAnchor = viewport.transforms.pageToScreenPoint(
            getMeasurementLeaderAnchor(measurement.geometry, labelPositionPage, {
              closed: !isOpenPathTool(measurement.tool),
            })
          );
          const showLeader = shouldShowMeasurementLeader(measurement.annotation);
          const strokeWidth = Math.max(2, measurement.annotation?.strokeWidth ?? 2);

          return (
            <g key={measurement._id}>
              {path.kind === "rect" ? (
                <rect
                  x={path.rect.x}
                  y={path.rect.y}
                  width={path.rect.width}
                  height={path.rect.height}
                  fill={hexToRgba(color, 0.1)}
                  stroke={color}
                  strokeWidth={2}
                  strokeDasharray="8 5"
                />
              ) : measurement.tool === TOOL_IDS.polygon ? (
                <polygon
                  points={path.points}
                  fill={hexToRgba(color, 0.08)}
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="8 5"
                />
              ) : (
                <polyline
                  points={path.points}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="8 5"
                />
              )}
              {showLeader ? (
                <line
                  x1={leaderAnchor.x}
                  y1={leaderAnchor.y}
                  x2={labelPosition.x}
                  y2={labelPosition.y}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                />
              ) : null}
            </g>
          );
        })}
      </svg>

      {layer.measurements.map((measurement) => {
        const tag = tagById.get(String(measurement.tagId ?? ""));
        const color = getTagColor(tag);
        const annotation: MeasurementAnnotation | undefined = measurement.annotation;
        const labelPosition = viewport.transforms.pageToScreenPoint(
          getMeasurementLabelPosition(measurement.geometry, annotation)
        );
        return (
          <div
            key={`${measurement._id}:label`}
            className="absolute -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background/95 px-2 py-1 text-center text-[11px] font-black leading-tight shadow-lg backdrop-blur"
            style={{
              left: labelPosition.x,
              top: labelPosition.y,
              borderColor: color,
              color: "var(--foreground)",
            }}
          >
            <div className="max-w-32 truncate">{tag?.rawValue ?? "Takeoff"}</div>
            <div>
              {formatDisplayedMeasurement(measurement.measurementType, measurement.value)} {measurement.measurementType}
            </div>
          </div>
        );
      })}
    </div>
  );
}
